import * as React from 'react'
import { Clock3, Globe2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from '@/components/ui/select'
import { DEFAULT_AGENT_TIMEZONE, getAvailableTimezones } from '@/lib/timezones'
import { cn } from '@/lib/utils'

type AgentTimezonePickerProps = {
  id: string
  label: string
  value: string
  onChange: (timezone: string) => void
  helpText?: string
  placeholder: string
  recommendedLabel: string
  allLabel: string
  currentTimeLabel: string
  defaultBadgeLabel: string
  disabled?: boolean
  required?: boolean
}

type TimezoneMeta = {
  displayLabel: string
  offsetLabel: string
}

function uniqueTimezones(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)))
}

function formatTimezoneDisplayLabel(timezone: string): string {
  const segments = timezone
    .split('/')
    .map((segment) => segment.replaceAll('_', ' '))
  return segments.join(' / ')
}

function formatTimezoneOffset(timezone: string, locale: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date())
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
    return offset.replace('GMT', 'UTC')
  } catch {
    return ''
  }
}

function formatTimezoneCurrentTime(timezone: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date())
  } catch {
    return ''
  }
}

export function AgentTimezonePicker({
  id,
  label,
  value,
  onChange,
  helpText,
  placeholder,
  recommendedLabel,
  allLabel,
  currentTimeLabel,
  defaultBadgeLabel,
  disabled = false,
  required = false,
}: AgentTimezonePickerProps) {
  const availableTimezones = React.useMemo(() => getAvailableTimezones(), [])
  const locale = React.useMemo(() => {
    if (typeof navigator !== 'undefined' && navigator.language) return navigator.language
    return 'en-US'
  }, [])
  const browserTimezone = React.useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return ''
    }
  }, [])

  const timezoneMeta = React.useMemo<Record<string, TimezoneMeta>>(
    () =>
      availableTimezones.reduce<Record<string, TimezoneMeta>>((accumulator, timezone) => {
        accumulator[timezone] = {
          displayLabel: formatTimezoneDisplayLabel(timezone),
          offsetLabel: formatTimezoneOffset(timezone, locale),
        }
        return accumulator
      }, {}),
    [availableTimezones, locale]
  )

  const recommendedTimezones = React.useMemo(() => {
    const initial = uniqueTimezones([
      value,
      browserTimezone,
      DEFAULT_AGENT_TIMEZONE,
      'UTC',
      'America/Argentina/Buenos_Aires',
      'America/Bogota',
      'America/Mexico_City',
      'America/New_York',
      'America/Los_Angeles',
      'Europe/London',
    ])

    return initial.filter((timezone) => availableTimezones.includes(timezone))
  }, [availableTimezones, browserTimezone, value])

  const recommendedSet = React.useMemo(() => new Set(recommendedTimezones), [recommendedTimezones])
  const allOtherTimezones = React.useMemo(
    () => availableTimezones.filter((timezone) => !recommendedSet.has(timezone)),
    [availableTimezones, recommendedSet]
  )

  const selectedTimezone = value || DEFAULT_AGENT_TIMEZONE
  const selectedMeta = timezoneMeta[selectedTimezone]
  const selectedCurrentTime = React.useMemo(
    () => formatTimezoneCurrentTime(selectedTimezone, locale),
    [selectedTimezone, locale]
  )
  const describedBy = helpText ? `${id}-help` : undefined

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>
          {label}
          {required ? ' *' : ''}
        </Label>
        {selectedTimezone === DEFAULT_AGENT_TIMEZONE && (
          <Badge variant="secondary" className="rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-[0.18em]">
            {defaultBadgeLabel}
          </Badge>
        )}
      </div>

      <Select value={selectedTimezone} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          id={id}
          aria-describedby={describedBy}
          className={cn(
            'h-auto rounded-lg border-border bg-background/60 px-4 py-3 text-left hover:bg-accent/40',
            disabled && 'cursor-not-allowed opacity-60'
          )}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary">
              <Globe2 className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <div className="block truncate font-mono text-sm text-foreground">
                {selectedTimezone || placeholder}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {selectedMeta?.displayLabel && <span className="truncate">{selectedMeta.displayLabel}</span>}
                {selectedMeta?.offsetLabel && <span>{selectedMeta.offsetLabel}</span>}
                {selectedCurrentTime && (
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" />
                    {currentTimeLabel}: {selectedCurrentTime}
                  </span>
                )}
              </div>
            </div>
          </div>
        </SelectTrigger>
        <SelectContent className="max-h-96 w-[min(440px,calc(100vw-2rem))]">
          <SelectGroup>
            <SelectLabel>{recommendedLabel}</SelectLabel>
            {recommendedTimezones.map((timezone) => {
              const meta = timezoneMeta[timezone]
              return (
                <SelectItem key={timezone} value={timezone} className="py-2.5">
                  <div className="min-w-0">
                    <div className="truncate font-mono text-sm">{timezone}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[meta?.displayLabel, meta?.offsetLabel].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </SelectItem>
              )
            })}
          </SelectGroup>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel>{allLabel}</SelectLabel>
            {allOtherTimezones.map((timezone) => {
              const meta = timezoneMeta[timezone]
              return (
                <SelectItem key={timezone} value={timezone} className="py-2.5">
                  <div className="min-w-0">
                    <div className="truncate font-mono text-sm">{timezone}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[meta?.displayLabel, meta?.offsetLabel].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </SelectItem>
              )
            })}
          </SelectGroup>
        </SelectContent>
      </Select>

      {helpText && (
        <p id={describedBy} className="text-xs text-muted-foreground">
          {helpText}
        </p>
      )}
    </div>
  )
}
